<!-- MonthlyFixedExpensesTracker.vue - Seguimiento mensual de gastos fijos -->
<template>
  <div class="monthly-fixed-expenses-tracker">
    <!-- Header con selector de mes -->
    <v-card class="mb-4" variant="outlined">
      <v-card-text>
        <v-row align="center">
          <v-col cols="auto">
            <v-btn icon="mdi-chevron-left" variant="text" @click="previousMonth" :disabled="loading"></v-btn>
          </v-col>
          <v-col cols="auto" class="text-center">
            <div class="text-h6 font-weight-medium">
              {{ monthNames[selectedMonth - 1] }} {{ selectedYear }}
            </div>
          </v-col>
          <v-col cols="auto">
            <v-btn icon="mdi-chevron-right" variant="text" @click="nextMonth" :disabled="loading || isCurrentOrFutureMonth"></v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-tooltip :text="generateButtonTooltip" location="bottom">
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    color="primary"
                    variant="tonal"
                    prepend-icon="mdi-calendar-sync"
                    :loading="generating"
                    :disabled="!canGenerateMonth"
                    @click="generateMonthlyExpenses"
                  >
                    Generar Mes
                  </v-btn>
                </span>
              </template>
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <v-btn
              icon="mdi-refresh"
              variant="text"
              @click="loadMonthlyExpenses"
              :loading="loading"
            ></v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Resumen del mes -->
    <v-row class="mb-4">
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="warning">
          <v-card-text class="text-center py-3">
            <div class="text-h5 font-weight-bold">{{ summary.count_pendiente || 0 }}</div>
            <div class="text-caption">Pendientes</div>
            <div class="text-body-2 font-weight-medium" v-if="summary.pendiente">
              RD$ {{ formatNumber(summary.pendiente) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="error">
          <v-card-text class="text-center py-3">
            <div class="text-h5 font-weight-bold">{{ summary.count_vencido || 0 }}</div>
            <div class="text-caption">Vencidos</div>
            <div class="text-body-2 font-weight-medium" v-if="summary.vencido">
              RD$ {{ formatNumber(summary.vencido) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="success">
          <v-card-text class="text-center py-3">
            <div class="text-h5 font-weight-bold">{{ summary.count_pagado || 0 }}</div>
            <div class="text-caption">Pagados</div>
            <div class="text-body-2 font-weight-medium" v-if="summary.total_pagado">
              RD$ {{ formatNumber(summary.total_pagado) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="outlined" color="info">
          <v-card-text class="text-center py-3">
            <div class="text-h5 font-weight-bold">RD$ {{ formatNumber(summary.total_estimado || 0) }}</div>
            <div class="text-caption">Total Estimado</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alertas de vencimiento -->
    <v-alert
      v-if="alerts.vencidos?.length"
      type="error"
      variant="tonal"
      class="mb-4"
      closable
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-alert-circle</v-icon>
        <span>
          <strong>{{ alerts.vencidos.length }}</strong> gastos vencidos requieren atención inmediata
        </span>
      </div>
    </v-alert>

    <v-alert
      v-if="alerts.alerts?.length"
      type="warning"
      variant="tonal"
      class="mb-4"
      closable
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-calendar-alert</v-icon>
        <span>
          <strong>{{ alerts.alerts.length }}</strong> gastos próximos a vencer en los próximos 7 días
        </span>
      </div>
    </v-alert>

    <!-- Lista de gastos del mes -->
    <v-card variant="outlined">
      <v-card-title class="d-flex align-center">
        <v-icon start>mdi-calendar-month</v-icon>
        Gastos Fijos del Mes
        <v-chip class="ml-2" size="small" color="info">
          {{ monthlyExpenses.length }} gastos
        </v-chip>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"></v-progress-linear>

        <v-list v-if="monthlyExpenses.length" lines="two">
          <v-list-item
            v-for="expense in sortedExpenses"
            :key="expense.id"
            class="mb-2 rounded border"
            :class="getExpenseClass(expense)"
          >
            <template v-slot:prepend>
              <v-avatar :color="getStatusColor(expense.estado)" size="40">
                <v-icon color="white">{{ getStatusIcon(expense.estado) }}</v-icon>
              </v-avatar>
            </template>

            <v-list-item-title>
              <strong>{{ expense.nombre }}</strong>
              <v-chip size="x-small" class="ml-2" :color="getCategoryColor(expense.categoria)">
                {{ getCategoryText(expense.categoria) }}
              </v-chip>
              <v-chip size="x-small" class="ml-1" :color="getStatusColor(expense.estado)" variant="flat">
                {{ getStatusText(expense.estado) }}
              </v-chip>
            </v-list-item-title>

            <v-list-item-subtitle>
              <div class="d-flex align-center flex-wrap ga-2">
                <span>
                  <v-icon size="14">mdi-store</v-icon>
                  {{ expense.agency?.name || 'N/A' }}
                </span>
                <span>
                  <v-icon size="14">mdi-currency-usd</v-icon>
                  RD$ {{ formatNumber(expense.monto_estimado) }}
                  <template v-if="expense.monto_real && expense.monto_real !== expense.monto_estimado">
                    → <strong>RD$ {{ formatNumber(expense.monto_real) }}</strong>
                  </template>
                </span>
                <span>
                  <v-icon size="14">mdi-calendar</v-icon>
                  Vence: {{ formatDate(expense.fecha_vencimiento) }}
                </span>
              </div>
              <div v-if="expense.estado === 'pagado' && expense.fecha_pago" class="text-caption text-success">
                <v-icon size="12">mdi-check-circle</v-icon>
                Pagado el {{ formatDate(expense.fecha_pago) }}
                <template v-if="expense.paidBy">
                  por {{ expense.paidBy.name }}
                </template>
                <!-- Botón para ver evidencia -->
                <v-btn
                  v-if="expense.comprobante_url"
                  variant="text"
                  size="x-small"
                  color="primary"
                  class="ml-2"
                  @click="openEvidenceDialog(expense)"
                >
                  <v-icon size="14">mdi-file-image</v-icon>
                  Ver evidencia
                </v-btn>
              </div>
              <div v-if="expense.notas" class="text-caption text-grey">
                <v-icon size="12">mdi-note</v-icon>
                {{ expense.notas }}
              </div>
            </v-list-item-subtitle>

            <template v-slot:append>
              <div class="d-flex align-center ga-1">
                <!-- Días restantes/vencidos -->
                <v-chip
                  v-if="expense.estado === 'pendiente' || expense.estado === 'vencido'"
                  size="x-small"
                  :color="getDaysColor(expense)"
                >
                  {{ getDaysLabel(expense) }}
                </v-chip>

                <!-- Botón registrar pago -->
                <v-btn
                  v-if="expense.estado === 'pendiente' || expense.estado === 'vencido'"
                  icon="mdi-cash-register"
                  size="small"
                  color="success"
                  variant="tonal"
                  @click="openPayDialog(expense)"
                  title="Registrar Pago"
                ></v-btn>

                <!-- Botón omitir -->
                <v-btn
                  v-if="expense.estado === 'pendiente'"
                  icon="mdi-cancel"
                  size="small"
                  variant="text"
                  color="grey"
                  @click="confirmSkip(expense)"
                  title="Omitir este mes"
                ></v-btn>
              </div>
            </template>
          </v-list-item>
        </v-list>

        <!-- Estado vacío -->
        <v-alert v-else-if="!loading" type="info" variant="tonal">
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-information</v-icon>
            <div>
              <div>No hay gastos fijos generados para este mes.</div>
              <div class="text-caption">
                Haz clic en "Generar Mes" para crear las entradas desde los gastos fijos configurados.
              </div>
            </div>
          </div>
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Diálogo: Registrar Pago -->
    <v-dialog v-model="payDialog.show" max-width="500" persistent>
      <v-card>
        <v-card-title class="d-flex align-center bg-success">
          <v-icon start color="white">mdi-cash-register</v-icon>
          <span class="text-white">Registrar Pago</span>
        </v-card-title>
        
        <v-card-text class="pt-4">
          <v-alert type="info" variant="tonal" class="mb-4">
            <strong>{{ payDialog.expense?.nombre }}</strong>
            <br>
            {{ payDialog.expense?.agency?.name }}
          </v-alert>

          <v-text-field
            v-model.number="payDialog.monto_real"
            type="number"
            label="Monto Pagado (RD$)"
            prepend-icon="mdi-currency-usd"
            :hint="`Estimado: RD$ ${formatNumber(payDialog.expense?.monto_estimado || 0)}`"
            persistent-hint
            min="0.01"
            step="0.01"
            density="compact"
            class="mb-3"
          ></v-text-field>

          <v-text-field
            v-model="payDialog.comprobante_numero"
            label="Número de Comprobante (opcional)"
            prepend-icon="mdi-receipt"
            density="compact"
            class="mb-3"
          ></v-text-field>

          <!-- Documento de Evidencia (OBLIGATORIO) -->
          <v-file-input
            v-model="payDialog.documento"
            label="Evidencia de Pago *"
            prepend-icon="mdi-camera"
            accept=".pdf,.jpg,.jpeg,.png"
            :hint="payDialog.documentoError ? '' : `Foto del recibo/factura (PDF o imagen, máx. ${MAX_FILE_SIZE_MB}MB)`"
            persistent-hint
            density="compact"
            class="mb-3"
            :error="!!payDialog.documentoError"
            :error-messages="payDialog.documentoError"
            :rules="[v => !!v || 'La evidencia es obligatoria para registrar el pago']"
            @update:model-value="handleDocumentoChange"
          >
            <template v-slot:selection="{ fileNames }">
              <v-chip 
                size="small" 
                :color="payDialog.documentoError ? 'error' : 'success'"
              >
                <v-icon start size="14">{{ payDialog.documentoError ? 'mdi-alert-circle' : 'mdi-file-check' }}</v-icon>
                {{ fileNames[0] }}
                <span v-if="payDialog.documentoSize" class="ml-1 text-caption">
                  ({{ payDialog.documentoSize }})
                </span>
              </v-chip>
            </template>
          </v-file-input>

          <!-- Alerta de archivo muy grande -->
          <v-alert
            v-if="payDialog.documentoError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-3"
            closable
            @click:close="payDialog.documentoError = null"
          >
            <template v-slot:prepend>
              <v-icon>mdi-file-alert</v-icon>
            </template>
            {{ payDialog.documentoError }}
            <div class="text-caption mt-1">
              💡 Tip: Reduce el tamaño de la imagen o usa un PDF comprimido.
            </div>
          </v-alert>

          <!-- Preview del documento si es imagen -->
          <v-img
            v-if="payDialog.documentoPreview && !payDialog.documentoError"
            :src="payDialog.documentoPreview"
            max-height="150"
            class="mb-3 rounded border"
            contain
          ></v-img>

          <v-textarea
            v-model="payDialog.notas"
            label="Notas (opcional)"
            prepend-icon="mdi-note"
            rows="2"
            density="compact"
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closePayDialog">Cancelar</v-btn>
          <v-btn
            color="success"
            variant="flat"
            :loading="payDialog.loading"
            :disabled="!canSubmitPayment"
            @click="submitPayment"
          >
            <v-icon start>mdi-check</v-icon>
            Confirmar Pago
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar Omitir -->
    <v-dialog v-model="skipDialog.show" max-width="400">
      <v-card>
        <v-card-title class="bg-grey-darken-1">
          <v-icon start color="white">mdi-cancel</v-icon>
          <span class="text-white">Omitir Gasto</span>
        </v-card-title>
        
        <v-card-text class="pt-4">
          <p>¿Estás seguro de omitir este gasto para este mes?</p>
          <v-alert type="warning" variant="tonal" class="mt-2">
            <strong>{{ skipDialog.expense?.nombre }}</strong>
            <br>
            RD$ {{ formatNumber(skipDialog.expense?.monto_estimado || 0) }}
          </v-alert>
          
          <v-textarea
            v-model="skipDialog.notas"
            label="Razón para omitir"
            prepend-icon="mdi-note"
            rows="2"
            density="compact"
            class="mt-3"
            placeholder="Ej: No aplica este mes, contrato terminado, etc."
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="skipDialog.show = false">Cancelar</v-btn>
          <v-btn
            color="grey-darken-1"
            variant="flat"
            :loading="skipDialog.loading"
            @click="submitSkip"
          >
            Confirmar Omitir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para ver evidencia de pago -->
    <v-dialog v-model="evidenceDialog.show" max-width="600">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-file-image</v-icon>
          Evidencia de Pago
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="evidenceDialog.show = false"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <div v-if="evidenceDialog.expense" class="mb-3">
            <p class="text-body-2 mb-1">
              <strong>{{ evidenceDialog.expense.nombre }}</strong>
            </p>
            <p class="text-caption text-grey">
              Pagado: RD$ {{ formatNumber(evidenceDialog.expense.monto_real || evidenceDialog.expense.monto_estimado) }}
              <span v-if="evidenceDialog.expense.comprobante_numero">
                - Comprobante #{{ evidenceDialog.expense.comprobante_numero }}
              </span>
            </p>
          </div>
          
          <!-- Mostrar imagen o enlace a PDF -->
          <div v-if="evidenceDialog.isPdf" class="text-center pa-4">
            <v-icon size="64" color="error">mdi-file-pdf-box</v-icon>
            <p class="mt-2">Documento PDF</p>
            <v-btn
              color="primary"
              :href="getFullUrl(evidenceDialog.imageUrl)"
              target="_blank"
            >
              <v-icon start>mdi-open-in-new</v-icon>
              Abrir PDF
            </v-btn>
          </div>
          <div v-else class="text-center">
            <v-img
              :src="getFullUrl(evidenceDialog.imageUrl)"
              max-height="400"
              contain
              class="rounded"
            >
              <template v-slot:error>
                <div class="d-flex align-center justify-center fill-height">
                  <v-icon size="48" color="grey">mdi-image-broken</v-icon>
                </div>
              </template>
            </v-img>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!evidenceDialog.isPdf"
            color="primary"
            variant="text"
            :href="getFullUrl(evidenceDialog.imageUrl)"
            target="_blank"
          >
            <v-icon start>mdi-open-in-new</v-icon>
            Abrir en nueva pestaña
          </v-btn>
          <v-btn variant="text" @click="evidenceDialog.show = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import ApiClient from '@/services/api/api-client'
import { getApiOriginUrl } from '@/config/runtimeConfig'

// Props
const props = defineProps({
  // Permite forzar un grupo específico (para SuperAdmin)
  groupId: {
    type: Number,
    default: null
  }
})

// Emits
const emit = defineEmits(['expense-paid', 'expense-skipped', 'month-generated'])

// State
const loading = ref(false)
const generating = ref(false)
const monthlyExpenses = ref([])
const summary = ref({})
const alerts = ref({ alerts: [], vencidos: [] })

const now = new Date()
const selectedYear = ref(now.getFullYear())
const selectedMonth = ref(now.getMonth() + 1)

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

// Constantes para validación de archivos
const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

// Diálogos
const payDialog = ref({
  show: false,
  loading: false,
  expense: null,
  monto_real: 0,
  comprobante_numero: '',
  notas: '',
  documento: null,
  documentoPreview: null,
  documentoBase64: null,
  documentoError: null,
  documentoSize: null
})

const skipDialog = ref({
  show: false,
  loading: false,
  expense: null,
  notas: ''
})

// Diálogo para ver evidencia de pago
const evidenceDialog = ref({
  show: false,
  expense: null,
  imageUrl: null,
  isPdf: false
})

// Snackbar
const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

// Computed
const isCurrentOrFutureMonth = computed(() => {
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  return selectedYear.value > currentYear || 
         (selectedYear.value === currentYear && selectedMonth.value >= currentMonth)
})

// Verificar si es un mes pasado (anterior al actual)
const isPastMonth = computed(() => {
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  return selectedYear.value < currentYear || 
         (selectedYear.value === currentYear && selectedMonth.value < currentMonth)
})

// Verificar si se puede generar el mes
const canGenerateMonth = computed(() => {
  // No se puede generar si:
  // 1. Ya hay gastos generados para el mes
  // 2. Es un mes anterior al actual
  return monthlyExpenses.value.length === 0 && !isPastMonth.value
})

// Tooltip para el botón de generar mes
const generateButtonTooltip = computed(() => {
  if (monthlyExpenses.value.length > 0) {
    return 'Ya existen gastos generados para este mes'
  }
  if (isPastMonth.value) {
    return 'No se pueden generar gastos para meses anteriores'
  }
  return 'Generar gastos fijos para este mes'
})

const sortedExpenses = computed(() => {
  return [...monthlyExpenses.value].sort((a, b) => {
    // Primero vencidos, luego pendientes, luego pagados, luego omitidos
    const order = { vencido: 0, pendiente: 1, pagado: 2, omitido: 3 }
    const orderDiff = (order[a.estado] || 4) - (order[b.estado] || 4)
    if (orderDiff !== 0) return orderDiff
    // Luego por fecha de vencimiento
    return new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento)
  })
})

// Methods
const formatNumber = (num) => {
  return new Intl.NumberFormat('es-DO').format(num || 0)
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return date.toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })
}

const previousMonth = () => {
  if (selectedMonth.value === 1) {
    selectedMonth.value = 12
    selectedYear.value--
  } else {
    selectedMonth.value--
  }
}

const nextMonth = () => {
  if (selectedMonth.value === 12) {
    selectedMonth.value = 1
    selectedYear.value++
  } else {
    selectedMonth.value++
  }
}

const getStatusColor = (status) => {
  const colors = {
    pendiente: 'warning',
    pagado: 'success',
    vencido: 'error',
    omitido: 'grey'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status) => {
  const icons = {
    pendiente: 'mdi-clock-outline',
    pagado: 'mdi-check-circle',
    vencido: 'mdi-alert-circle',
    omitido: 'mdi-close-circle'
  }
  return icons[status] || 'mdi-help-circle'
}

const getStatusText = (status) => {
  const texts = {
    pendiente: 'Pendiente',
    pagado: 'Pagado',
    vencido: 'Vencido',
    omitido: 'Omitido'
  }
  return texts[status] || status
}

const getCategoryColor = (categoria) => {
  const colors = {
    alquiler: 'purple',
    servicios: 'blue',
    comunicaciones: 'cyan',
    seguros: 'orange',
    mantenimiento: 'brown',
    otros: 'grey'
  }
  return colors[categoria] || 'grey'
}

const getCategoryText = (categoria) => {
  const texts = {
    alquiler: 'Alquiler',
    servicios: 'Servicios',
    comunicaciones: 'Comunicaciones',
    seguros: 'Seguros',
    mantenimiento: 'Mantenimiento',
    otros: 'Otros'
  }
  return texts[categoria] || categoria
}

const getExpenseClass = (expense) => {
  if (expense.estado === 'vencido') return 'border-error'
  if (expense.estado === 'pendiente') return 'border-warning'
  if (expense.estado === 'pagado') return 'border-success'
  return ''
}

const getDaysLabel = (expense) => {
  if (!expense.fecha_vencimiento) return ''
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const vencimiento = new Date(expense.fecha_vencimiento)
  vencimiento.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((vencimiento - today) / (1000 * 60 * 60 * 24))
  
  if (diffDays < 0) return `${Math.abs(diffDays)} días vencido`
  if (diffDays === 0) return 'Vence hoy'
  if (diffDays === 1) return 'Vence mañana'
  return `${diffDays} días`
}

const getDaysColor = (expense) => {
  if (!expense.fecha_vencimiento) return 'grey'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const vencimiento = new Date(expense.fecha_vencimiento)
  vencimiento.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((vencimiento - today) / (1000 * 60 * 60 * 24))
  
  if (diffDays < 0) return 'error'
  if (diffDays <= 3) return 'warning'
  return 'info'
}

// API calls
const loadMonthlyExpenses = async () => {
  loading.value = true
  try {
    const params = { anio: selectedYear.value, mes: selectedMonth.value }
    console.log('📅 Cargando gastos mensuales:', params)
    const response = await ApiClient.get('/field-supervisor/monthly-fixed-expenses', { params })
    console.log('📅 Respuesta gastos mensuales:', response)
    
    // ApiClient.get() retorna response.data directamente (ya desencapsulado)
    // response = { success: true, data: { expenses, summary, periodo } }
    if (response?.success) {
      monthlyExpenses.value = response.data?.expenses || []
      summary.value = response.data?.summary || {}
      console.log('📅 Expenses cargados:', monthlyExpenses.value.length, 'Summary:', summary.value)
    } else {
      console.warn('📅 Respuesta sin success:', response)
    }
  } catch (error) {
    console.error('Error loading monthly expenses:', error)
    showSnackbar('Error al cargar gastos mensuales', 'error')
  } finally {
    loading.value = false
  }
}

const loadAlerts = async () => {
  try {
    const response = await ApiClient.get('/field-supervisor/monthly-fixed-expenses/alerts')
    // ApiClient.get() retorna response.data directamente
    if (response?.success) {
      alerts.value = response.data || { alerts: [], vencidos: [] }
    }
  } catch (error) {
    console.error('Error loading alerts:', error)
  }
}

const generateMonthlyExpenses = async () => {
  generating.value = true
  try {
    const response = await ApiClient.post('/field-supervisor/monthly-fixed-expenses/generate', {
      anio: selectedYear.value,
      mes: selectedMonth.value
    })
    
    if (response.data?.success) {
      const result = response.data.data
      showSnackbar(`Generados ${result.creados} gastos, ${result.existentes} ya existían`, 'success')
      emit('month-generated', result)
      await loadMonthlyExpenses()
    }
  } catch (error) {
    console.error('Error generating monthly expenses:', error)
    showSnackbar(error.response?.data?.error || 'Error al generar gastos', 'error')
  } finally {
    generating.value = false
  }
}

const openPayDialog = (expense) => {
  payDialog.value = {
    show: true,
    loading: false,
    expense,
    monto_real: expense.monto_estimado,
    comprobante_numero: '',
    notas: '',
    documento: null,
    documentoPreview: null,
    documentoBase64: null,
    documentoError: null,
    documentoSize: null
  }
}

const closePayDialog = () => {
  payDialog.value.show = false
  payDialog.value.documento = null
  payDialog.value.documentoPreview = null
  payDialog.value.documentoBase64 = null
  payDialog.value.documentoError = null
}

// Validación del formulario de pago
const canSubmitPayment = computed(() => {
  return payDialog.value.monto_real > 0 && 
         payDialog.value.documento && 
         !payDialog.value.documentoError
})

// Formatear tamaño de archivo
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

// Verificar si es imagen
const isImageFile = (file) => {
  if (!file) return false
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png']
  return imageTypes.includes(file.type)
}

// Manejar cambio de documento
const handleDocumentoChange = async (file) => {
  // Resetear estados
  payDialog.value.documentoPreview = null
  payDialog.value.documentoBase64 = null
  payDialog.value.documentoError = null
  payDialog.value.documentoSize = null
  
  if (!file) {
    return
  }
  
  // Guardar tamaño para mostrar al usuario
  payDialog.value.documentoSize = formatFileSize(file.size)
  
  // Validar tamaño del archivo
  if (file.size > MAX_FILE_SIZE_BYTES) {
    payDialog.value.documentoError = `El archivo es demasiado grande (${formatFileSize(file.size)}). Máximo permitido: ${MAX_FILE_SIZE_MB}MB.`
    payDialog.value.documento = null
    console.warn(`⚠️ Archivo rechazado: ${file.name} excede el límite de ${MAX_FILE_SIZE_MB}MB`)
    return
  }
  
  // Crear preview para imágenes
  if (isImageFile(file)) {
    const reader = new FileReader()
    reader.onload = (e) => {
      payDialog.value.documentoPreview = e.target.result
    }
    reader.readAsDataURL(file)
  }
  
  // Convertir a base64 para enviar al servidor
  const base64Reader = new FileReader()
  base64Reader.onload = (e) => {
    const base64 = e.target.result.split(',')[1]
    payDialog.value.documentoBase64 = base64
  }
  base64Reader.readAsDataURL(file)
}

const submitPayment = async () => {
  // Validar que hay evidencia
  if (!payDialog.value.documento) {
    showSnackbar('La evidencia de pago es obligatoria', 'error')
    return
  }
  
  payDialog.value.loading = true
  try {
    // Crear FormData para enviar archivo
    const formData = new FormData()
    formData.append('monto_real', payDialog.value.monto_real)
    formData.append('comprobante_numero', payDialog.value.comprobante_numero || '')
    formData.append('notas', payDialog.value.notas || '')
    formData.append('evidencia', payDialog.value.documento)
    
    const response = await ApiClient.post(
      `/field-supervisor/monthly-fixed-expenses/${payDialog.value.expense.id}/pay`,
      formData
    )
    
    if (response.data?.success) {
      showSnackbar('Pago registrado correctamente con evidencia', 'success')
      emit('expense-paid', response.data.data)
      closePayDialog()
      await loadMonthlyExpenses()
      await loadAlerts()
    }
  } catch (error) {
    console.error('Error submitting payment:', error)
    showSnackbar(error.response?.data?.error || 'Error al registrar pago', 'error')
  } finally {
    payDialog.value.loading = false
  }
}

const confirmSkip = (expense) => {
  skipDialog.value = {
    show: true,
    loading: false,
    expense,
    notas: ''
  }
}

const submitSkip = async () => {
  skipDialog.value.loading = true
  try {
    const response = await ApiClient.put(
      `/field-supervisor/monthly-fixed-expenses/${skipDialog.value.expense.id}/skip`,
      { notas: skipDialog.value.notas }
    )
    
    if (response.data?.success) {
      showSnackbar('Gasto omitido', 'info')
      emit('expense-skipped', response.data.data)
      skipDialog.value.show = false
      await loadMonthlyExpenses()
    }
  } catch (error) {
    console.error('Error skipping expense:', error)
    showSnackbar(error.response?.data?.error || 'Error al omitir gasto', 'error')
  } finally {
    skipDialog.value.loading = false
  }
}

// Abrir diálogo para ver evidencia de pago
const openEvidenceDialog = (expense) => {
  const url = expense.comprobante_url
  evidenceDialog.value = {
    show: true,
    expense,
    imageUrl: url,
    isPdf: url?.toLowerCase().endsWith('.pdf')
  }
}

// Obtener URL completa para archivos
const getFullUrl = (path) => {
  if (!path) return ''
  // Si ya es una URL completa, devolverla
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  return `${getApiOriginUrl()}${path}`
}

const showSnackbar = (message, color = 'success') => {
  snackbar.value = { show: true, message, color }
}

// Watchers
watch([selectedYear, selectedMonth], () => {
  loadMonthlyExpenses()
})

// Lifecycle
onMounted(() => {
  loadMonthlyExpenses()
  loadAlerts()
})
</script>

<style scoped>
.monthly-fixed-expenses-tracker {
  padding: 16px;
}

.border-error {
  border-left: 4px solid rgb(var(--v-theme-error)) !important;
}

.border-warning {
  border-left: 4px solid rgb(var(--v-theme-warning)) !important;
}

.border-success {
  border-left: 4px solid rgb(var(--v-theme-success)) !important;
}

.ga-1 {
  gap: 4px;
}

.ga-2 {
  gap: 8px;
}
</style>
